import {
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
  type QueryConstraint,
} from 'firebase/firestore'
import * as Sentry from '@sentry/react'
import { appointmentDayRunsCollection } from './firestore'
import type { AppointmentDayRun } from '../types'

// ── Types ───────────────────────────────────────────────────────────

/** A data source whose sync can fail. Revolution first (#128); VSP/EyeMed
 *  plug in here later without touching the banner surface. */
export type SyncSource = 'revolution'

export const SYNC_SOURCE_LABELS: Record<SyncSource, string> = {
  revolution: 'Revolution appointments',
}

/** One clinic-day's currently-active sync failure, ready for display. */
export interface SyncFailure {
  runId: string
  clinicId: string
  source: SyncSource
  /** The appointment date the failed pull was for (MM/DD/YYYY —
   *  Revolution's run-doc format, parsed via parseFirestoreDateStr). */
  appointmentDate: string
  /** When the last attempt failed (best available timestamp). */
  failedAt: Date | null
  /** Total attempts made (retryCount + 1). */
  attempts: number
  /** Raw error message for the detail view — never shown in the chip. */
  errorDetail: string | null
  /** Most recent successful sync for this clinic inside the window, if any. */
  lastSuccessAt: Date | null
  /** Changes whenever a new attempt fails, so a dismissal stops applying
   *  the moment the next cycle fails again. */
  dismissKey: string
}

/** Run statuses that mean "this pull is done and the data arrived". */
const SUCCESS_STATUSES: ReadonlySet<AppointmentDayRun['status']> = new Set([
  'pulled',
  'processing',
  'completed',
])

// ── Pure selection logic (exported for tests) ───────────────────────

/** Parsed view of a run doc — only what failure selection needs. */
export interface RunForSelection {
  id: string
  clinicId: string
  appointmentDate: string
  status: AppointmentDayRun['status']
  /** Last sync activity: createdAt is re-stamped by prepareRetries on every
   *  attempt, so it orders runs by "when the pipeline last touched this". */
  activityAt: Date | null
  failedAt: Date | null
  succeededAt: Date | null
  retryCount: number
  errorDetail: string | null
}

/**
 * How long a `timeout` run may sit after its stamped `failedAt` before it
 * stops counting as "mid-retry" and surfaces as a failure. A `timeout` run
 * (the clock fallback on a record of the retired engine) is re-collected by
 * the hourly pull's ladder like a `failed` one; 30 minutes keeps the chip
 * from firing on a run whose retry is still coming while catching one two
 * sweep cycles have passed over.
 */
export const STRANDED_GRACE_AFTER_TIMEOUT_MS = 30 * 60 * 1000

/**
 * Grace for `failed` runs. A failed Revolution run (`dispatch_failed`, or a
 * worker-reported failure) is only retried by the next hourly
 * `scheduledAppointmentPull`, so it can legitimately sit at `failed` for up
 * to ~60 minutes while a retry is still coming. Invariant: MUST exceed that
 * hourly retry cadence, or the chip goes red mid-cycle and then clears when
 * the hourly retry lands (false alarm / flap).
 */
export const STRANDED_GRACE_AFTER_FAILED_MS = 75 * 60 * 1000

/**
 * Fallback grace for legacy run docs without a `failedAt` stamp, keyed off
 * `activityAt` (claim-time createdAt). A lane execution can legitimately
 * run for hours after a run's claim before the failsafe net finalizes a
 * task that never reported — the frozen-task bound is every platform
 * attempt plus grace (`GCP_FROZEN_TASK_BOUND_MS`, ~4h at the budget
 * ceiling) — and a grace at or below that fires the instant the finalize
 * lands and then flaps when the next pull re-collects (exactly the big
 * clinics with long pulls). Invariant: MUST exceed that bound.
 */
export const STRANDED_GRACE_FALLBACK_MS = 4.5 * 60 * 60 * 1000

/** Decided failure: retries exhausted (`error`), or a `failed`/`timeout`
 *  run the pipeline hasn't touched within the stranded grace period —
 *  measured from `failedAt` when stamped, else the raised legacy fallback. */
function isDecidedFailure(r: RunForSelection, now: number): boolean {
  if (r.status === 'error') return true
  if (r.status === 'failed' || r.status === 'timeout') {
    if (r.failedAt) {
      const grace = r.status === 'timeout'
        ? STRANDED_GRACE_AFTER_TIMEOUT_MS
        : STRANDED_GRACE_AFTER_FAILED_MS
      return now - r.failedAt.getTime() > grace
    }
    const activity = r.activityAt?.getTime()
    return activity != null && now - activity > STRANDED_GRACE_FALLBACK_MS
  }
  return false
}

/**
 * Per clinic-DAY (the run doc's natural identity): the latest decided run
 * wins. A run is decided once it has delivered data (pulled/processing/
 * completed), exhausted its retries (`error`), or sat stranded at
 * `failed`/`timeout` past the grace period. Fresh `failed`/`timeout` runs
 * are mid-retry and do NOT fire the banner — the "don't cry wolf on the
 * transient flake" rule from #128.
 *
 * Grouping is clinic+date, not clinic: a terminal failure for July 12 is a
 * permanent day-level data gap, and a success for July 13 must not clear
 * it — only THAT day's run succeeding does. (The popover names the affected
 * day, so the clearing rule matches the story it tells.)
 */
export function selectSyncFailures(
  runs: RunForSelection[],
  now: number = Date.now(),
): SyncFailure[] {
  // Clinic-level "last success" context for the popover, across all dates.
  const lastSuccessByClinic = new Map<string, Date>()
  for (const run of runs) {
    if (!run.clinicId || !SUCCESS_STATUSES.has(run.status)) continue
    const at = run.succeededAt ?? run.activityAt
    if (!at) continue
    const prev = lastSuccessByClinic.get(run.clinicId)
    if (!prev || at.getTime() > prev.getTime()) lastSuccessByClinic.set(run.clinicId, at)
  }

  const byDay = new Map<string, RunForSelection[]>()
  for (const run of runs) {
    if (!run.clinicId) continue
    const key = `${run.clinicId}|${run.appointmentDate}`
    const list = byDay.get(key)
    if (list) list.push(run)
    else byDay.set(key, [run])
  }

  const failures: SyncFailure[] = []
  for (const dayRuns of byDay.values()) {
    const decided = dayRuns
      .filter(r => isDecidedFailure(r, now) || SUCCESS_STATUSES.has(r.status))
      .sort((a, b) => (b.activityAt?.getTime() ?? 0) - (a.activityAt?.getTime() ?? 0))
    const latest = decided[0]
    if (!latest || SUCCESS_STATUSES.has(latest.status)) continue

    failures.push({
      runId: latest.id,
      clinicId: latest.clinicId,
      source: 'revolution',
      appointmentDate: latest.appointmentDate,
      failedAt: latest.failedAt ?? latest.activityAt,
      attempts: latest.retryCount + 1,
      errorDetail: latest.errorDetail,
      lastSuccessAt: lastSuccessByClinic.get(latest.clinicId) ?? null,
      // runId alone can't distinguish failure cycles: the run doc id is the
      // deterministic clinicId_date re-claimed on every attempt, and every
      // exhausted cycle ends at the same retryCount — so a later cycle for
      // the same day would reproduce the key and stay hidden behind an old
      // dismissal. failedAt is stamped fresh on every failed attempt (and
      // cleared on each retry claim; legacy docs fall back to activityAt,
      // re-stamped per claim), so including it makes each cycle's key unique.
      dismissKey: `${latest.id}:${latest.retryCount}:${(latest.failedAt ?? latest.activityAt)?.getTime() ?? 0}`,
    })
  }

  // Stable order: most recent failure first.
  return failures.sort(
    (a, b) => (b.failedAt?.getTime() ?? 0) - (a.failedAt?.getTime() ?? 0),
  )
}

// ── Firestore subscription ──────────────────────────────────────────

function toDate(val: unknown): Date | null {
  if (!val) return null
  if (val instanceof Timestamp) return val.toDate()
  if (typeof val === 'object' && 'toDate' in val) {
    return (val as { toDate(): Date }).toDate()
  }
  if (typeof val === 'string') {
    const d = new Date(val)
    return Number.isNaN(d.getTime()) ? null : d
  }
  return null
}

export const SYNC_FAILURE_WINDOW_DAYS = 7

/**
 * Field-mapping layer: run doc → the shape failure selection consumes.
 * Exported for tests — a renamed backend field would otherwise fail
 * silently (the selector just sees nulls).
 */
export function mapRunDoc(id: string, data: Record<string, unknown>): RunForSelection {
  const run = data as Partial<AppointmentDayRun>
  return {
    id,
    clinicId: typeof run.clinicId === 'string' ? run.clinicId : '',
    appointmentDate: typeof run.appointmentDate === 'string' ? run.appointmentDate : '',
    status: run.status as AppointmentDayRun['status'],
    activityAt: toDate(data.createdAt),
    failedAt: toDate(data.failedAt),
    succeededAt: toDate(data.pulledAt),
    retryCount: typeof data.retryCount === 'number' ? data.retryCount : 0,
    errorDetail:
      typeof data.lastError === 'string' && data.lastError
        ? data.lastError
        : typeof data.error === 'string' && data.error
          ? data.error
          : null,
  }
}

/** Re-run failure selection with a fresh clock this often — a run crosses
 *  the stranded-grace threshold by time passing, not by a Firestore push
 *  (a clinic whose pulls stopped entirely never gets a new snapshot). */
export const RESELECT_INTERVAL_MS = 5 * 60 * 1000

/** Recreate the query once its frozen windowStart is a day stale, so a
 *  weeks-open tab doesn't accumulate an ever-growing result set. */
export const RESUBSCRIBE_AFTER_MS = 24 * 60 * 60 * 1000

/** Listener-error retry backoff: 30s doubling to a 10-min cap, forever —
 *  Firestore permanently stops a listener after an error, and a monitoring
 *  surface must not stay dead-but-looking-healthy for the session. */
export const RETRY_BACKOFF_INITIAL_MS = 30 * 1000
export const RETRY_BACKOFF_MAX_MS = 10 * 60 * 1000

/**
 * Live-subscribes to recent appointmentDayRuns and reports active sync
 * failures per clinic — the same onSnapshot mechanism the Cockpit already
 * uses, pointed at the run collection (per #128). Admins see every clinic
 * in the org; clinic-scoped users see their own (mirrors subscribeToRpaJobs,
 * and firestore.rules `clinicMatch` enforces the same split server-side).
 *
 * Beyond the raw listener it owns three time-driven concerns: a 5-min
 * re-selection tick (grace thresholds cross by clock, not by snapshot), a
 * daily query refresh (fresh 7-day window), and bounded resubscribe-with-
 * backoff after a listener error (Firestore kills errored listeners).
 */
export function subscribeToSyncFailures(
  orgId: string,
  clinicId: string | null,
  isAdmin: boolean,
  onData: (failures: SyncFailure[]) => void,
): () => void {
  let stopped = false
  let unsubscribeSnapshot: (() => void) | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let retryDelayMs = RETRY_BACKOFF_INITIAL_MS
  let subscribedAtMs = 0
  let lastRuns: RunForSelection[] = []

  function subscribe() {
    subscribedAtMs = Date.now()
    const windowStart = new Date(subscribedAtMs - SYNC_FAILURE_WINDOW_DAYS * 24 * 60 * 60 * 1000)

    const constraints: QueryConstraint[] = []
    if (!isAdmin && clinicId) {
      constraints.push(where('clinicId', '==', clinicId))
    }
    constraints.push(
      where('createdAt', '>=', Timestamp.fromDate(windowStart)),
      orderBy('createdAt', 'desc'),
    )

    const runsQuery = query(appointmentDayRunsCollection(orgId), ...constraints)
    unsubscribeSnapshot = onSnapshot(runsQuery, (snap) => {
      retryDelayMs = RETRY_BACKOFF_INITIAL_MS // healthy again — reset backoff
      lastRuns = snap.docs.map((d) => mapRunDoc(d.id, d.data() as unknown as Record<string, unknown>))
      onData(selectSyncFailures(lastRuns))
    }, (err) => {
      Sentry.captureException(err)
      // The listener is dead after an error — don't leave the chip frozen on
      // stale failures, and schedule a resubscribe so it isn't dead forever.
      unsubscribeSnapshot?.()
      unsubscribeSnapshot = null
      lastRuns = []
      onData([])
      if (stopped) return
      retryTimer = setTimeout(() => {
        retryTimer = null
        if (!stopped) subscribe()
      }, retryDelayMs)
      retryDelayMs = Math.min(retryDelayMs * 2, RETRY_BACKOFF_MAX_MS)
    })
  }

  const interval = setInterval(() => {
    if (stopped || !unsubscribeSnapshot) return // error retry in flight — nothing to advance
    if (Date.now() - subscribedAtMs > RESUBSCRIBE_AFTER_MS) {
      unsubscribeSnapshot()
      unsubscribeSnapshot = null
      subscribe() // fresh window; its first snapshot re-pushes data
      return
    }
    onData(selectSyncFailures(lastRuns, Date.now()))
  }, RESELECT_INTERVAL_MS)

  subscribe()

  return () => {
    stopped = true
    clearInterval(interval)
    if (retryTimer) clearTimeout(retryTimer)
    unsubscribeSnapshot?.()
    unsubscribeSnapshot = null
  }
}

// ── Dismissal persistence ───────────────────────────────────────────
// localStorage keyed per run doc (clinic+date): dismissing hides the
// *current* failure only. A new failed attempt produces a new dismissKey
// (runId:retryCount:failedAt), so a still-failing sync resurfaces on its
// next cycle — "dismissible, but honest" (#128). Bounded: every write prunes
// entries whose run is no longer in the current failure set, so the map
// never outgrows one key per active clinic-day.

/** Exported so the chip can react to `storage` events from other tabs. */
export const DISMISS_STORAGE_KEY = 'vitalrev.syncFailureDismissals'

export function readDismissals(): Record<string, string> {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .filter(([, v]) => typeof v === 'string'),
    ) as Record<string, string>
  } catch {
    return {}
  }
}

/**
 * Persist dismissals for `failures`, pruning stored entries whose run is no
 * longer in `currentFailures` (defaults to the dismissed batch). Passing the
 * FULL current failure set matters: it keeps a still-failing sibling's
 * earlier dismissal alive while dropping keys for runs that left the 7-day
 * window — the pruning that keeps the map bounded.
 */
export function dismissFailures(
  failures: SyncFailure[],
  currentFailures: SyncFailure[] = failures,
): Record<string, string> {
  const liveRunIds = new Set(currentFailures.map(f => f.runId))
  for (const f of failures) liveRunIds.add(f.runId)
  const next: Record<string, string> = {}
  for (const [runId, key] of Object.entries(readDismissals())) {
    if (liveRunIds.has(runId)) next[runId] = key
  }
  for (const f of failures) next[f.runId] = f.dismissKey
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Storage full/unavailable — dismissal just won't persist across reloads.
  }
  return next
}

/** Failures the user hasn't dismissed (or whose dismissal went stale). */
export function activeFailures(
  failures: SyncFailure[],
  dismissals: Record<string, string>,
): SyncFailure[] {
  return failures.filter(f => dismissals[f.runId] !== f.dismissKey)
}
